import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  GlobeAltIcon, 
  BuildingOfficeIcon, 
  CreditCardIcon,
  CubeIcon,
  CheckCircleIcon,
  ClockIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import AnimatedImage from '../ui/AnimatedImage';
import bookingImg from '../../assets/booking.png';
import resturantImg from '../../assets/resturant.png';

export default function Solutions() {
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [demoFormData, setDemoFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    product: '',
    message: ''
  });
  const [contactFormData, setContactFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const currentProducts = [
    {
      id: 'channel-manager',
      name: 'Channel Manager',
      description: 'Streamline your OTA bookings with our comprehensive channel management platform. Manage multiple booking channels from a single dashboard.',
      features: ['Multi-channel integration', 'Real-time availability sync', 'Automated rate management', 'Performance analytics'],
      icon: GlobeAltIcon,
      image: bookingImg,
      color: 'from-blue-500 to-cyan-500'
    },
    {
      id: 'hotel-management',
      name: 'Hotel Management Software',
      description: 'Complete hotel management solution covering reservations, check-in, billing, and comprehensive reporting for modern hospitality businesses.',
      features: ['Reservation management', 'Guest check-in/out', 'Billing & invoicing', 'Advanced reporting'],
      icon: BuildingOfficeIcon,
      image: resturantImg,
      color: 'from-green-500 to-emerald-500'
    }
  ];

  const upcomingProducts = [
    {
      id: 'billing-software',
      name: 'Billing Software',
      description: 'Simplified invoicing and payment processing designed for hospitality and retail businesses.',
      features: ['Automated invoicing', 'Payment processing', 'Financial reporting', 'Multi-currency support'],
      icon: CreditCardIcon,
      color: 'from-purple-500 to-pink-500'
    },
    {
      id: 'inventory-management',
      name: 'Inventory Management',
      description: 'Comprehensive stock tracking and management system for hotels and retail operations.',
      features: ['Real-time tracking', 'Automated reordering', 'Stock alerts', 'Analytics dashboard'],
      icon: CubeIcon,
      color: 'from-orange-500 to-red-500'
    }
  ];

  const handleContactSales = () => {
    setShowContactModal(true);
  };

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      setSuccessMessage('Demo request submitted successfully! Our team will contact you within 24 hours to schedule your personalized demo.');
      setShowSuccessDialog(true);
      setShowDemoModal(false);
      setDemoFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        product: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting demo request:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      setSuccessMessage('Message sent successfully! Our sales team will get back to you within 24 hours.');
      setShowSuccessDialog(true);
      setShowContactModal(false);
      setContactFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting contact form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDemoFormChange = useCallback((e) => {
    const { name, value } = e.target;
    setDemoFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const handleContactFormChange = useCallback((e) => {
    const { name, value } = e.target;
    setContactFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  // Modal Components
  const DemoModal = () => (
    <div className={`fixed inset-0 z-[60] flex items-center justify-center p-4 ${showDemoModal ? 'block' : 'hidden'}`}>
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowDemoModal(false)}></div>
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl relative z-10"
      >
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold text-gray-900">Request Demo</h3>
          <button
            onClick={() => setShowDemoModal(false)}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        
        <form onSubmit={handleDemoSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
            <input
              type="text"
              name="name"
              required
              value={demoFormData.name}
              onChange={handleDemoFormChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
              placeholder="Enter your full name"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
            <input
              type="email"
              name="email"
              required
              value={demoFormData.email}
              onChange={handleDemoFormChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
              placeholder="Enter your email"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
            <input
              type="text"
              name="company"
              value={demoFormData.company}
              onChange={handleDemoFormChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
              placeholder="Enter your company name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
            <input
              type="tel"
              name="phone"
              value={demoFormData.phone}
              onChange={handleDemoFormChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
              placeholder="Enter your phone number"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Product Interest</label>
            <input
              type="text"
              name="product"
              value={demoFormData.product}
              onChange={handleDemoFormChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
              placeholder="Which product are you interested in?"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Additional Message</label>
            <textarea
              name="message"
              rows={3}
              value={demoFormData.message}
              onChange={handleDemoFormChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 resize-none"
              placeholder="Tell us about your needs..."
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full py-3 px-6 rounded-xl font-semibold transition-all duration-200 ${
              isSubmitting
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-primary-600 to-secondary-600 text-white hover:shadow-lg transform hover:-translate-y-0.5'
            }`}
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Submitting...</span>
              </div>
            ) : (
              'Request Demo'
            )}
          </button>
        </form>
      </motion.div>
    </div>
  );

  const ContactModal = () => (
    <div className={`fixed inset-0 z-[60] flex items-center justify-center p-4 ${showContactModal ? 'block' : 'hidden'}`}>
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowContactModal(false)}></div>
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl relative z-10"
      >
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold text-gray-900">Contact Sales</h3>
          <button
            onClick={() => setShowContactModal(false)}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        
        <form onSubmit={handleContactSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
            <input
              type="text"
              name="name"
              required
              value={contactFormData.name}
              onChange={handleContactFormChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
              placeholder="Enter your full name"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
            <input
              type="email"
              name="email"
              required
              value={contactFormData.email}
              onChange={handleContactFormChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
              placeholder="Enter your email"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
            <input
              type="text"
              name="company"
              value={contactFormData.company}
              onChange={handleContactFormChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
              placeholder="Enter your company name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
            <input
              type="tel"
              name="phone"
              value={contactFormData.phone}
              onChange={handleContactFormChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
              placeholder="Enter your phone number"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
            <textarea
              name="message"
              rows={4}
              required
              value={contactFormData.message}
              onChange={handleContactFormChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 resize-none"
              placeholder="Tell us about your requirements..."
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full py-3 px-6 rounded-xl font-semibold transition-all duration-200 ${
              isSubmitting
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-primary-600 to-secondary-600 text-white hover:shadow-lg transform hover:-translate-y-0.5'
            }`}
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Sending...</span>
              </div>
            ) : (
              'Send Message'
            )}
          </button>
        </form>
      </motion.div>
    </div>
  );

  const SuccessDialog = () => (
    <div className={`fixed inset-0 z-[60] flex items-center justify-center p-4 ${showSuccessDialog ? 'block' : 'hidden'}`}>
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowSuccessDialog(false)}></div>
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl relative z-10"
      >
        <div className="text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100 mb-6">
            <CheckCircleIcon className="h-8 w-8 text-green-600" />
          </div>
          
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Success!
          </h3>
          
          <p className="text-gray-600 mb-6">
            {successMessage}
          </p>
          
          <button
            onClick={() => setShowSuccessDialog(false)}
            className="w-full bg-gradient-to-r from-primary-600 to-secondary-600 text-white py-3 px-6 rounded-xl font-semibold hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200"
          >
            Got it, thanks!
          </button>
        </div>
      </motion.div>
    </div>
  );

  return (
    <section id="solutions" className="py-24 sm:py-32 bg-white">
      <DemoModal />
      <ContactModal />
      <SuccessDialog />
      
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-base font-semibold leading-7 text-primary-600">Our Solutions</h2>
            <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Complete Business Management Suite
            </p>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              From channel management to inventory control, our comprehensive suite of solutions 
              empowers businesses to operate more efficiently and profitably.
            </p>
          </motion.div>
        </div>

        {/* Current Products */}
        <div className="mt-16 sm:mt-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Current Products</h3>
            <div className="flex items-center justify-center space-x-2">
              <CheckCircleIcon className="h-5 w-5 text-green-500" />
              <span className="text-sm font-medium text-green-600">Available Now</span>
            </div>
          </motion.div>

          <div className="grid gap-8 lg:grid-cols-2">
            {currentProducts.map((product, index) => (
              <motion.div
                key={product.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="group relative bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300"
              >
                <div className="p-8">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-r ${product.color}`}>
                      <product.icon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h4 className="text-xl font-bold text-gray-900">{product.name}</h4>
                      <div className="flex items-center space-x-2 mt-1">
                        <CheckCircleIcon className="h-4 w-4 text-green-500" />
                        <span className="text-sm text-green-600 font-medium">Available</span>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-gray-600 mb-6">{product.description}</p>
                  
                  <div className="space-y-3 mb-6">
                    {product.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center space-x-3">
                        <div className="flex-shrink-0 w-2 h-2 bg-primary-500 rounded-full"></div>
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <Link 
                    to={`/products?product=${product.id}`}
                    className="w-full bg-gradient-to-r from-primary-600 to-secondary-600 text-white py-3 px-6 rounded-xl font-semibold hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 inline-block text-center"
                  >
                    Learn More
                  </Link>
                </div>
                
                {product.image && (
                  <div className="p-8 pt-0">
                    <AnimatedImage
                      src={product.image}
                      alt={`${product.name} Screenshot`}
                      animationType="zoomIn"
                      className="w-full rounded-xl shadow-md border border-gray-100"
                    />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>

        {/* Upcoming Products */}
        <div className="mt-24 sm:mt-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Coming Soon</h3>
            <div className="flex items-center justify-center space-x-2">
              <ClockIcon className="h-5 w-5 text-accent-500" />
              <span className="text-sm font-medium text-accent-600">In Development</span>
            </div>
          </motion.div>

          <div className="grid gap-8 lg:grid-cols-2">
            {upcomingProducts.map((product, index) => (
              <motion.div
                key={product.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="group relative bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300"
              >
                <div className="absolute top-4 right-4">
                  <span className="inline-flex items-center rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">
                    <ClockIcon className="h-3 w-3 mr-1" />
                    Coming Soon
                  </span>
                </div>

                <div className="p-8">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-r ${product.color} opacity-80`}>
                      <product.icon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h4 className="text-xl font-bold text-gray-900">{product.name}</h4>
                      <div className="flex items-center space-x-2 mt-1">
                        <ClockIcon className="h-4 w-4 text-accent-500" />
                        <span className="text-sm text-accent-600 font-medium">In Development</span>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-gray-600 mb-6">{product.description}</p>
                  
                  <div className="space-y-3 mb-6">
                    {product.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center space-x-3">
                        <div className="flex-shrink-0 w-2 h-2 bg-accent-400 rounded-full"></div>
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <Link 
                    to={`/products?product=${product.id}`}
                    className="w-full bg-gray-200 text-gray-600 py-3 px-6 rounded-xl font-semibold cursor-not-allowed inline-block text-center"
                  >
                    Coming Soon
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-24 sm:mt-32 text-center"
        >
          <div className="bg-gradient-to-r from-primary-600 to-secondary-600 rounded-3xl p-8 sm:p-12">
            <h3 className="text-2xl font-bold text-white mb-4">
              Ready to Transform Your Business?
            </h3>
            <p className="text-primary-100 mb-8 max-w-2xl mx-auto">
              Join hundreds of businesses already using Autiin solutions to streamline their operations 
              and boost their bottom line.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/products"
                className="bg-white text-primary-600 px-8 py-3 rounded-xl font-semibold hover:bg-gray-50 transform hover:-translate-y-0.5 transition-all duration-200 inline-block"
              >
                View All Products
              </Link>
              <button
                onClick={handleContactSales}
                className="border-2 border-white text-white px-8 py-3 rounded-xl font-semibold hover:bg-white hover:text-primary-600 transform hover:-translate-y-0.5 transition-all duration-200 inline-block"
              >
                Contact Sales
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
} 